import { useSaveBillablePrice } from '@domains/subscription/priv/api/queries'
import { useCreateBillableTemplatePrice } from '@domains/subscription/priv/Billables/api/query'
import type { BillableFormPluginProps, PriceRowPlugin } from '@domains/subscription/priv/Billables/usecases/BillableForm/plugin'
import type { CreatePricePlugin, CreatePricePluginProps } from '@domains/subscription/priv/Billables/usecases/CreatePriceForm'
import { LinearPriceForm } from '@domains/subscription/priv/Billables/usecases/Price/linear'
import { LinearPriceRow } from '@domains/subscription/priv/Billables/usecases/Price/linear/LinearPriceRow'
import type { LinearPrice, LinearPriceRequest } from '@domains/subscription/priv/Billables/usecases/Price/linear/linear'
import { isLinear, mapBillableToLinearPriceForm } from '@domains/subscription/priv/Billables/usecases/Price/linear/linearMapper'
import type { BillablePrice } from '@domains/subscription/priv/types'
import { type ComponentType, memo, useCallback } from 'react'

const LinearPriceFormFactory = (props: CreatePricePluginProps) => {
  const { mutate } = useCreateBillableTemplatePrice(props.billableId, props.templateId)

  const existingPrices = mapBillableToLinearPriceForm(props.price as BillablePrice)

  const handleCreatePrice = (linearPriceRequest: LinearPriceRequest) => {
    mutate(linearPriceRequest, {
      onSuccess: () => {
        props.onCreatedPrice(linearPriceRequest)
      },
    })
  }

  return <LinearPriceForm {...props} defaultPrice={existingPrices} onCreatePrice={handleCreatePrice} />
}

const LinearPriceFormComponent = (props: CreatePricePluginProps) => {
  const existingPrices = mapBillableToLinearPriceForm(props.price as BillablePrice)

  const handleCreatePrice = (linearPriceRequest: LinearPriceRequest) => {
    props.onCreatedPrice(linearPriceRequest)
  }

  return <LinearPriceForm {...props} defaultPrice={existingPrices} onCreatePrice={handleCreatePrice} />
}

export class LinearCreatePricePlugin implements CreatePricePlugin {
  type = 'Linear'
  supports(type): boolean {
    return type === 'Linear'
  }
  Component: ComponentType<CreatePricePluginProps> = LinearPriceFormFactory
  FormComponent: ComponentType<CreatePricePluginProps> = LinearPriceFormComponent
}

const LinearPriceRowFactory = memo((props: BillableFormPluginProps) => {
  const { mutateAsync: saveBillable } = useSaveBillablePrice(props.templateId)

  const handleSave = useCallback(
    async (data: LinearPrice) => {
      await saveBillable({
        billableId: data.billableId,
        billablePrice: data,
      })
    },
    [saveBillable],
  )

  if (!isLinear(props.price)) {
    return null
  }

  return <LinearPriceRow {...props} linearPrice={props.price} onSubmit={handleSave} />
})

const LinearPriceRowFactoryComponent = memo((props: BillableFormPluginProps) => {
  if (!isLinear(props.price)) {
    return null
  }

  return <LinearPriceRow {...props} linearPrice={props.price} onSubmit={noopAsync} />
})

export class LinearPriceRowPlugin implements PriceRowPlugin {
  type = 'Linear'

  supports(algorithm): boolean {
    return algorithm.type === this.type
  }
  Component: ComponentType<BillableFormPluginProps> = LinearPriceRowFactory
  FormComponent: ComponentType<BillableFormPluginProps> = LinearPriceRowFactoryComponent
}

const noopAsync = async () => {}
